import { IAttachment, IAudit } from '@alberta/konexi-shared';
import { AssetLoadable, ImageItem } from '@alberta/swodoc-document-generator';
import { Inject } from '@angular/core';
import { Filesystem } from '@capacitor/filesystem';
import {
  ATTACHMENT_LOCAL_PREFIX,
  AttachmentServiceToken,
  IAttachmentService,
} from '@services/attachment/attachment-service.interface';
import { AttachmentTypeService } from '@services/attachment/attachment-type.service';
import { AttachmentComponentModel } from 'src/app/business/attachment/attachment-component-model';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';

export interface IAttachmentWithData extends IAttachment {
  data: string;
  dataPath?: string;
}

export class DocumentAttachmentService implements AssetLoadable {
  public loadedAssets: IAttachmentWithData[];

  private _imagesToAdd: Partial<IAttachmentWithData>[] = [];
  private _imagesToRemove: string[] = [];

  constructor(
    private readonly attachmentModel: AttachmentComponentModel,
    @Inject(AttachmentServiceToken) private _attachmentService: IAttachmentService,
    private readonly attachmentTypeService: AttachmentTypeService
  ) {}

  public getImagesForNodeId(id: string): Partial<IAttachmentWithData>[] {
    const initLoadedAttachments = this.loadedAssets.filter(item => item.metadata.auditNodeId === id || item._id === id);
    const withoutRemovedAttachments = initLoadedAttachments.filter(attachment => {
      const att = this._imagesToRemove.find(removedId => removedId === attachment._id);
      return att ? false : true;
    });
    const newAttachments = this._imagesToAdd.filter(item => item.metadata.auditNodeId === id);
    return [...withoutRemovedAttachments, ...newAttachments];
  }

  public addImage(attachment: Partial<IAttachmentWithData>): void {
    if (!this._imagesToAdd.find(attachmentToAdd => attachmentToAdd._id === attachment._id)) {
      this._imagesToAdd.push(attachment);
    }
  }

  public removeImage(id: string): void {
    if (id.startsWith(ATTACHMENT_LOCAL_PREFIX)) {
      this._imagesToAdd = this._imagesToAdd.filter(attachment => attachment._id !== id);
    }
    if (!this._imagesToRemove.find(imageId => imageId === id)) {
      this._imagesToRemove.push(id);
    }
  }

  public async initAttachments(audit: IAudit) {
    const assets = await this.getAssets(audit);
    this.loadedAssets = await this.mapAssets(assets);
    if (audit.images) {
      this._imagesToAdd = [...audit.images];
    }
  }

  public async load(): Promise<ImageItem[]> {
    const withOutRemovedAttachments = this.loadedAssets.filter(attachment => {
      const att = this._imagesToRemove.find(removedId => removedId === attachment._id);
      return att ? false : true;
    }) as any[];
    return [...withOutRemovedAttachments, ...this._imagesToAdd];
  }

  public async writeAttachmentToDb(
    audit: IAudit,
    saveAllImagesToDB: boolean,
    isLinkedToInstitutionDocument = false
  ): Promise<{ id: string }[]> {
    await this.removeImages();
    return this.saveImages(audit, saveAllImagesToDB, isLinkedToInstitutionDocument);
  }

  public signatureImages(): IAttachmentWithData[] {
    return this._imagesToAdd.filter(item => item.metadata.type === 102) as IAttachmentWithData[]; // 102 = SwodocSignature
  }

  public removeSignatures(): void {
    this.loadedAssets.filter(item => item.metadata.type === 102).forEach(item => this.removeImage(item._id)); // 102 = SwodocSignature
    this._imagesToAdd.filter(item => item.metadata.type === 102).forEach(item => this.removeImage(item._id));
  }

  private async removeImages(): Promise<void> {
    await Promise.all(
      this._imagesToRemove.map(imageId =>
        this._attachmentService.delete(imageId).catch(error => window.logger.error('Error deleting images', error))
      )
    );
  }

  private async saveImages(
    audit: IAudit,
    saveAllImagesToDB: boolean,
    isLinkedToInstitutionDocument: boolean
  ): Promise<{ id: string }[]> {
    const createdImages: Promise<{ id: string }>[] = [];
    if (saveAllImagesToDB) {
      for (const image of this._imagesToAdd) {
        const createdImage = this.createImage(image, audit, isLinkedToInstitutionDocument);
        createdImages.push(createdImage);
      }
      return Promise.all(createdImages);
    } else {
      const filteredImages: any[] = [];
      for (const image of this._imagesToAdd) {
        if (image.metadata.type !== 102) {
          // Not SwodocSignature
          filteredImages.push(image);
        }
      }
      for (const image of filteredImages) {
        const createdImage = this.createImage(image, audit, isLinkedToInstitutionDocument);
        createdImages.push(createdImage);
      }
      return Promise.all(createdImages);
    }
  }

  private async createImage(
    attachment: Partial<IAttachmentWithData>,
    audit: IAudit,
    isLinkedToInstitutionDocument = false
  ): Promise<{ id: string }> {
    const attachmentType = await this.attachmentTypeService.getAttachmentTypeValue(attachment.metadata.type);
    const attachmentMetadata = {
      name: `swodoc_${audit._id}_${attachmentType}_${attachment.metadata.auditNodeId}.jpeg`,
      auditId: audit._id,
      auditNodeId: attachment.metadata.auditNodeId,
      patientId: audit.patientId,
      type: attachment.metadata.type,
      regionId: audit.regionId,
      isLinkedToInstitutionDocument,
      relatedEntity: 'audit' as const,
    };

    const imageData = attachment.dataPath ? await this.readFileAsBase64(attachment.dataPath) : attachment.data;

    const uploadResult = await this._attachmentService.create(imageData, attachmentMetadata);
    if (uploadResult.error) {
      window.logger.error('createImage failed', uploadResult.error);
    }

    return uploadResult.data;
  }

  private async readFileAsBase64(filePath: string): Promise<string> {
    const fileData = await Filesystem.readFile({ path: filePath });
    const base64 = fileData.data as string;
    // Ensure it has the data URL prefix
    return base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
  }

  private async getAssets(audit: IAudit): Promise<Array<Partial<IAttachment>>> {
    const assets = await new Promise<AttachmentDto[]>(resolve => {
      this.attachmentModel
        .search(`metadata.auditId:${audit._id} metadata.templateId:${audit.templateId}`, {
          isIn: true,
          ignoreRegionIds: true,
          ignoreOnlineSearch: true,
        })
        .subscribe(items => resolve(items));
    });
    await this._attachmentService.save(assets);

    return assets as unknown as IAttachment[];
  }

  private async mapAssets(assets: Array<Partial<IAttachment>>): Promise<IAttachmentWithData[]> {
    return Promise.all(
      assets.map(async asset => {
        const b64 = (await this._attachmentService.get(asset._id, 'base64')) as string;
        return { ...asset, data: b64 } as IAttachmentWithData;
      })
    );
  }
}
